import './productConfiguration.scss';

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import produce from 'utils/produce';
import classNames from 'classnames';
import { Card, Typography, Alert, Badge, Form } from 'components/base';
import useDataContext from 'data/context/data';
import { formatPrice } from 'utils/format';
import uniq from 'utils/uniq';
import {
  isListOfColoredDisplayProducts,
  getColoredDisplayProductIds,
  getRelatedProductsIds,
} from 'data/lib/product';
import productReducer from 'data/lib/productReducer';
import i18next from 'internationalization';

import ConfigurationSection from './productConfiguration/configurationSection';
import OptionConfigurator from './productConfiguration/optionConfigurator';

export const prepareBundleData = (dataContext, configuration) => {
  if (configuration.bundle) {
    return {
      bundle: {
        id: configuration.bundle.id,
        name: configuration.bundle.name,
        secondaryProducts: dataContext
          .getSkuProducts(configuration.secondaryProductIds)
          .map(secondaryProduct => ({
            id: secondaryProduct.id,
            name: secondaryProduct.name,
            version: secondaryProduct.version,
            data: configuration.secondaryProductData,
          })),
      },
    };
  }
  if (configuration.upsell) {
    return {
      upsell: {
        id: configuration.upsell.id,
        name: configuration.upsell.name,
        version: configuration.upsell.version,
        data: configuration.secondaryProductData,
      },
    };
  }
  return {};
};

const ProductConfiguration = props => {
  const DEFAULT_SECONDARY_OPTION = props.defaultPrintOnly ? -1 : 0;

  const dataContext = useDataContext();

  const [configuration, setConfiguration] = useState();
  const configurationRef = useRef({});

  const [priceData, setPriceData] = useState();
  useEffect(() => {
    if (priceData) {
      props.onChange(priceData);
    }
  }, [priceData]);

  useEffect(() => {
    configurationRef.current = configuration ?? {};
  }, [configuration]);

  const [primaryProducts, setPrimaryProducts] = useState([]);
  const [secondaryProductOptions, setSecondaryProductOptions] = useState([]);
  const [upsellOptions, setUpsellOptions] = useState([]);

  const handleOnChangeConfiguration = (id, value) => {
    if (props.product.reducer) {
      const isProductValid = productReducer(props.product.reducer, props.product.options, {
        ...configuration,
        [id]: value,
      });

      if (!isProductValid) {
        const availableConfig = productReducer(props.product.reducer, props.product.options, {
          ...configuration,
          [id]: value,
          firstAvailable: value,
        });

        if (availableConfig) {
          setConfiguration({
            ...availableConfig,
            secondaryIndex: configurationRef.current.secondaryIndex ?? DEFAULT_SECONDARY_OPTION,
          });
        } else {
          alert('Sorry, this variant is not available.');
        }
        return;
      }
    }

    setConfiguration(
      produce(configuration, draft => {
        draft[id] = value;
      }),
    );
  };

  const handleOnChangeOptions = (key, value) => {
    setConfiguration(
      produce(configuration, draft => {
        draft[key] = value;
      }),
    );
  };

  useEffect(() => {
    const config = {};
    if (props.product.reducer) {
      productReducer(props.product.reducer, props.product.options).forEach(({ key, values }) => {
        config[key] = values[0];
      });
    } else {
      const primaryProductIds = [];
      props.product.options.forEach(option => {
        primaryProductIds.push(...option.products.primary);
      });

      setPrimaryProducts(
        uniq(primaryProductIds)
          .map(dataContext.getSkuProduct)
          .filter(product => !!product)
          .sort((a, b) => a.categoryPosition - b.categoryPosition),
      );

      config.primaryIndex = 0;
    }

    config.secondaryIndex = configurationRef.current.secondaryIndex ?? DEFAULT_SECONDARY_OPTION;

    setConfiguration(config);
  }, [props.product]);

  useEffect(() => {
    let primaryProduct;

    const data = {};
    if (configuration) {
      if (props.product.reducer) {
        const value = productReducer(props.product.reducer, props.product.options, configuration);
        primaryProduct = value;
        let config = configuration;
        if (Array.isArray(value)) {
          primaryProduct = value[0];
          config = value[1];
        }
        data.config = config;
      } else {
        primaryProduct = primaryProducts[configuration.primaryIndex];
        data.config = configuration;
        if (configuration.primaryData && Object.keys(configuration.primaryData).length > 0) {
          data.primaryProductData = {
            options: configuration.primaryData,
          };
        }
      }

      data.productId = primaryProduct.id;
      data.product = primaryProduct;

      let quantity = configuration.quantity ?? 1;
      let price = primaryProduct.price;

      if (primaryProduct.priceTiers) {
        const defaultTier = Object.keys(primaryProduct.priceTiers)[0];
        const currentTierPrice = primaryProduct.priceTiers[`${quantity}`];

        if (currentTierPrice != null) {
          price = currentTierPrice;
        } else if (defaultTier != null) {
          quantity = parseInt(defaultTier, 10);
          price = primaryProduct.priceTiers[defaultTier];
        }
      }

      data.productPrice = price;
      data.quantity = quantity;
      data.price = price;

      // BUNDLES
      if (
        configuration.secondaryIndex >= 0 &&
        secondaryProductOptions.length > configuration.secondaryIndex
      ) {
        data.bundle = secondaryProductOptions[configuration.secondaryIndex];
        data.secondaryProductIds = data.bundle.products.secondary;
        data.price = data.bundle.price;
        if (configuration.secondaryData && Object.keys(configuration.secondaryData).length > 0) {
          if (isListOfColoredDisplayProducts(data.bundle.products.secondary)) {
            // filter by options, and there are no data - new approach
            data.secondaryProductIds = getColoredDisplayProductIds(
              data.bundle.products.secondary,
              configuration.secondaryData,
            );
            data.secondaryProductData = undefined;
          } else {
            // dont't filter and options are represented as data - old approach
            data.secondaryProductData = {
              options: configuration.secondaryData,
            };
          }
        }
      }

      // UPSELL
      if (
        configuration.secondaryIndex >= 0 &&
        upsellOptions.length > configuration.secondaryIndex
      ) {
        const upsellOption = upsellOptions[configuration.secondaryIndex];

        if (configuration.secondaryData && Object.keys(configuration.secondaryData).length > 0) {
          if (isListOfColoredDisplayProducts(upsellOption.skus)) {
            // filter by options, and there are no data - new approach
            const upsellSkuProduct = upsellOption.skuProducts.find(
              p =>
                getColoredDisplayProductIds(upsellOption.skus, configuration.secondaryData).indexOf(
                  p.id,
                ) >= 0,
            );
            if (upsellSkuProduct) {
              data.upsell = upsellSkuProduct;
              data.price += upsellSkuProduct.price;
              data.secondaryProductData = undefined;
            }
          } else {
            // dont't filter and options are represented as data - old approach
            const upsellSkuProduct = upsellOption.skuProducts[0];
            data.upsell = upsellSkuProduct;
            data.price += upsellSkuProduct.price;
            data.secondaryProductData = {
              options: configuration.secondaryData,
            };
          }
        }
      }
    }

    setPriceData(data);
  }, [props.product, primaryProducts, secondaryProductOptions, configuration]);

  useEffect(() => {
    if (configuration && props.skuProduct) {
      const primaryProduct = props.skuProduct;

      const secondaryProductOptionsIds = [];
      props.product.bundles.forEach(productOption => {
        if (productOption.products.primary.indexOf(primaryProduct.id) >= 0) {
          secondaryProductOptionsIds.push(productOption.id);
        }
      });

      setSecondaryProductOptions(
        uniq(secondaryProductOptionsIds)
          .map(dataContext.getSkuProduct)
          .filter(product => !!product && product.products.secondary.length > 0)
          .filter(
            product =>
              dataContext.getSkuProducts(
                product.products.secondary.filter(
                  id => getRelatedProductsIds(primaryProduct).indexOf(id) >= 0,
                ),
              ).length > 0,
          )
          .sort((a, b) => a.categoryPosition - b.categoryPosition),
      );

      setUpsellOptions(props.product.upsell);
    }
  }, [primaryProducts, configuration, props.skuProduct]);

  if (!configuration || props.hidden) {
    return null;
  }

  const renderBundleSection = primaryProduct => {
    const renderPrintOnlyCard = (render = false) => {
      if (!render) {
        return null;
      }

      // diffPrice - when priceData is undefined, keep it 0
      const diffPrice = priceData ? primaryProduct.price - priceData.price : 0;

      return (
        <Card
          asLink
          onClick={e => {
            e.preventDefault();
            handleOnChangeConfiguration('secondaryIndex', -1);
          }}
          className={classNames({
            active:
              configuration.secondaryIndex === -1 ||
              configuration.secondaryIndex >= secondaryProductOptions.length,
            [props.cardClassName]: props.cardClassName,
          })}
        >
          <div className="card-inline split">
            <Typography.H6>
              {i18next.t(`printsonly.${primaryProduct.id}`, 'Prints Only')}
            </Typography.H6>
            <strong className={props.priceLabelClassName}>
              {diffPrice === 0 ? (
                <>&nbsp;</>
              ) : (
                <>
                  {diffPrice > 0 && '+'}
                  {formatPrice(diffPrice, primaryProduct.currency, true)}
                </>
              )}
            </strong>
          </div>
        </Card>
      );
    };

    return (
      <>
        <Typography.Span block className="label text-light">
          {`Select an option`.toUpperCase()}
        </Typography.Span>
        <div
          className="card-list"
          data-items-in-row={Math.min(2, secondaryProductOptions.length + 1)}
        >
          {renderPrintOnlyCard(props.defaultPrintOnly && !props.hidePrintOnly)}

          {secondaryProductOptions.map((p, index) => {
            /**
             * Math.min for minimal discount
             * Math.max for maximum discount
             */
            const savings =
              Math.min(
                ...p.products.secondary
                  .map(secondaryId => dataContext.getPrice(secondaryId))
                  .filter(price => !!price)
                  .map(({ price }) => price),
              ) +
              primaryProduct.price -
              p.price;

            // diffPrice - when priceData is undefined, keep it 0
            const diffPrice = p.price - priceData.price;

            return (
              <Card
                key={p.id}
                asLink
                onClick={e => {
                  e.preventDefault();
                  handleOnChangeConfiguration('secondaryIndex', index);
                }}
                className={classNames({
                  active: configuration.secondaryIndex === index,
                  [props.cardClassName]: props.cardClassName,
                })}
              >
                <div className="card-inline split">
                  <Typography.H6>{p.secondaryCaption}</Typography.H6>
                  <strong className={props.priceLabelClassName}>
                    {diffPrice === 0 ? (
                      <>&nbsp;</>
                    ) : (
                      <>
                        {diffPrice > 0 && '+'}
                        {formatPrice(diffPrice, p.currency, true)}
                      </>
                    )}
                  </strong>
                </div>
                <Badge type="success" pill className="badge-config-sm">
                  RECOMMENDED{savings > 0 && `. SAVE ${formatPrice(savings, p.currency)}!`}
                </Badge>
              </Card>
            );
          })}

          {renderPrintOnlyCard(!props.defaultPrintOnly && !props.hidePrintOnly)}
        </div>

        <OptionConfigurator
          primaryProduct={primaryProduct}
          products={
            configuration.secondaryIndex >= 0 &&
            secondaryProductOptions.length > configuration.secondaryIndex
              ? dataContext.getSkuProducts(
                  secondaryProductOptions[configuration.secondaryIndex].products.secondary,
                  // { type: 'BUNDLE_SECONDARY_PRODUCTS', primaryId: primaryProduct?.id }, // unused custom transformer
                )
              : []
          }
          minNumberOfItems={props.minNumberOfItems}
          onChange={value => handleOnChangeOptions('secondaryData', value)}
          cardClassName={props.cardClassName}
          labels={props.labels}
        />
      </>
    );
  };

  const renderUpsellSection = primaryProduct => {
    const renderPrintOnlyCard = (render = false) => {
      if (!render) {
        return null;
      }

      const diffPrice = 0;

      return (
        <Card
          asLink
          onClick={e => {
            e.preventDefault();
            handleOnChangeConfiguration('secondaryIndex', -1);
          }}
          className={classNames({
            active:
              configuration.secondaryIndex === -1 ||
              configuration.secondaryIndex >= upsellOptions.length,
            [props.cardClassName]: props.cardClassName,
          })}
        >
          <div className="card-inline split">
            <Typography.H6>
              {i18next.t(`printsonly.${primaryProduct.id}`, 'Prints Only')}
            </Typography.H6>
            <strong className={props.priceLabelClassName}>
              {diffPrice === 0 ? (
                <>&nbsp;</>
              ) : (
                <>
                  {diffPrice > 0 && '+'}
                  {formatPrice(diffPrice, primaryProduct.currency, true)}
                  {primaryProduct.priceTiers && ` Per ${props.productType}`}
                </>
              )}
            </strong>
          </div>
        </Card>
      );
    };

    return (
      <>
        <Typography.Span block className="label text-light">
          {`Select an option`.toUpperCase()}
        </Typography.Span>
        <div className="card-list" data-items-in-row={Math.min(2, upsellOptions.length + 1)}>
          {renderPrintOnlyCard(props.defaultPrintOnly && !props.hidePrintOnly)}

          {upsellOptions.map((upsell, index) => {
            const diffPrice = Math.min(...upsell.skuProducts.map(p => p.price));

            return (
              <Card
                key={upsell.label}
                asLink
                onClick={e => {
                  e.preventDefault();
                  handleOnChangeConfiguration('secondaryIndex', index);
                }}
                className={classNames({
                  active: configuration.secondaryIndex === index,
                  [props.cardClassName]: props.cardClassName,
                })}
              >
                <div className="card-inline split">
                  <Typography.H6>{upsell.label}</Typography.H6>
                  <strong className={classNames(props.priceLabelClassName, 'text-right')}>
                    {diffPrice === 0 ? (
                      <>&nbsp;</>
                    ) : (
                      <>
                        {diffPrice > 0 && '+'}
                        {formatPrice(diffPrice, primaryProduct.currency, true)}
                        {primaryProduct.priceTiers && ` Per ${props.productType}`}
                      </>
                    )}
                  </strong>
                </div>
                {upsell.recommended && (
                  <Badge type="success" pill className="badge-config-sm">
                    RECOMMENDED
                  </Badge>
                )}
              </Card>
            );
          })}

          {renderPrintOnlyCard(!props.defaultPrintOnly && !props.hidePrintOnly)}
        </div>

        <OptionConfigurator
          primaryProduct={primaryProduct}
          products={
            configuration.secondaryIndex >= 0 && upsellOptions.length > configuration.secondaryIndex
              ? upsellOptions[configuration.secondaryIndex].skuProducts
              : []
          }
          minNumberOfItems={props.minNumberOfItems}
          onChange={value => handleOnChangeOptions('secondaryData', value)}
          cardClassName={props.cardClassName}
          labels={props.labels}
        />
      </>
    );
  };

  const renderQuantitySection = primaryProduct => {
    if (!primaryProduct.priceTiers) {
      return null;
    }

    const values = Object.keys(primaryProduct.priceTiers);

    return (
      <div className="configuration-row mb-2">
        <Typography.Span block className="label text-light">
          QUANTITY
        </Typography.Span>
        <Form.Select
          onChange={e => handleOnChangeConfiguration('quantity', parseInt(e.target.value, 10))}
        >
          {values.map(option => (
            <Form.SelectOption key={option} value={option}>
              {`${option}  -  ${formatPrice(
                primaryProduct.priceTiers[option],
                primaryProduct.currency,
              )} Per ${props.productType}`}
            </Form.SelectOption>
          ))}
        </Form.Select>
      </div>
    );
  };

  if (props.product.reducer) {
    return (
      <>
        {
          productReducer(props.product.reducer, props.product.options).reduce(
            (acc, { key, label, sort, values, unavailable, infoOnly, availableOnly }) => {
              if (infoOnly || values.length === 1) {
                acc.children.push(
                  <div key={key} className="configuration-cell">
                    <Typography.H6>{label.toUpperCase()}</Typography.H6>
                    <Typography.H3 className="mb-3">{configuration[key]}</Typography.H3>
                  </div>,
                );
              } else if (values.length > 1) {
                const isOptionDisabled = value => {
                  if (availableOnly) {
                    const val = productReducer(props.product.reducer, props.product.options, {
                      ...acc.selected,
                      [key]: value,
                      isPossible: true,
                    });

                    return !val;
                  }
                  return false;
                };

                acc.children.push(
                  <ConfigurationSection
                    key={key}
                    templateRootPath={props.skuProduct.templateRootPath}
                    id={key}
                    label={`Select ${label}`}
                    options={values
                      .map(value => ({
                        id: value,
                        label: value,
                        disabled: isOptionDisabled(value),
                      }))
                      .sort(({ label: a }, { label: b }) => {
                        if (sort === 'asc') {
                          return a < b ? -1 : 1;
                        }
                        if (sort === 'desc') {
                          return a > b ? -1 : 1;
                        }
                        return 0;
                      })}
                    unavailable={unavailable}
                    selectedOptionId={configuration[key]}
                    onChange={handleOnChangeConfiguration}
                    cardClassName={props.cardClassName}
                    description={props.product.config.variantDescription}
                    autoItemsInRow={props.autoItemsWidth}
                  />,
                );
              }

              acc.selected[key] = configuration[key];
              return acc;
            },
            { selected: {}, children: [] },
          ).children
        }

        {secondaryProductOptions.length > 0 && (
          <div className="configuration-row">{renderBundleSection(props.skuProduct)}</div>
        )}

        {renderQuantitySection(props.skuProduct)}

        {upsellOptions.length > 0 && (
          <div className="configuration-row">{renderUpsellSection(props.skuProduct)}</div>
        )}

        {props.showSkuDescription && props.skuProduct.description && (
          <Alert type="warning">{props.skuProduct.description}</Alert>
        )}
      </>
    );
  }

  const primaryProduct = props.skuProduct;

  if (!primaryProduct) {
    return null;
  }

  return (
    <>
      {primaryProducts.length > 1 && (
        <>
          <ConfigurationSection
            product={props.product}
            id="primaryIndex"
            templateRootPath={primaryProduct.templateRootPath}
            label={primaryProduct.primaryCaption}
            options={primaryProducts.map((p, index) => ({
              id: index,
              label: p.templateMetaData.name,
            }))}
            selectedOptionId={configuration.primaryIndex}
            onChange={handleOnChangeConfiguration}
            cardClassName={props.cardClassName}
            description={props.product.config.variantDescription}
            autoItemsInRow={props.autoItemsWidth}
          />
        </>
      )}

      <OptionConfigurator
        primaryProduct={undefined}
        products={[primaryProduct]}
        onChange={value => handleOnChangeOptions('primaryData', value)}
        cardClassName={props.cardClassName}
        labels={props.labels}
      />

      {secondaryProductOptions.length > 0 && <>{renderBundleSection(primaryProduct)}</>}

      {renderQuantitySection(primaryProduct)}

      {upsellOptions.length > 0 && (
        <div className="configuration-row">{renderUpsellSection(primaryProduct)}</div>
      )}

      {props.showSkuDescription && primaryProduct.description && (
        <Alert type="warning">{primaryProduct.description}</Alert>
      )}
    </>
  );
};

ProductConfiguration.propTypes = {
  product: PropTypes.object,
  skuProduct: PropTypes.object,
  onChange: PropTypes.func,
  cardClassName: PropTypes.string,
  priceLabelClassName: PropTypes.string,
  defaultPrintOnly: PropTypes.bool,
  hidePrintOnly: PropTypes.bool,
  minNumberOfItems: PropTypes.number,
  hidden: PropTypes.bool,
  autoItemsWidth: PropTypes.bool,
  showSkuDescription: PropTypes.bool,
  labels: PropTypes.object,
  productType: PropTypes.string,
};

ProductConfiguration.defaultProps = {
  priceLabelClassName: 'text-info',
  hidden: false,
  defaultPrintOnly: false,
  minNumberOfItems: 2,
  hidePrintOnly: false,
  autoItemsWidth: false,
  showSkuDescription: false,
  labels: {},
  productType: 'Print',
};

export default ProductConfiguration;
